import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { UpdateCard } from './UpdateCard';
import { useStakeholderUpdates } from '../useGovernment';
import { plural } from '../format';
import type { ApplyUpdateInput } from '../types';

/**
 * What research thinks has changed about this person, on their own page.
 *
 * It sits above their roles because that is what applying one changes: an
 * appointment ends a role and opens another, and the reviewer should see both
 * in one screen.
 */
export const StakeholderUpdates: React.FC<{
  stakeholderId: string;
  canEdit: boolean;
  onApplied?: () => void;
}> = ({ stakeholderId, canEdit, onApplied }) => {
  const { updates, busy, act } = useStakeholderUpdates(stakeholderId);
  const [error, setError] = useState<string | null>(null);

  const pending = updates.filter((update) => update.status === 'pending');
  // What was just applied or dismissed here stays, saying what it did
  const shown = updates.filter((update) => update.status === 'pending' || update.reviewedAt);

  if (shown.length === 0) return null;

  const apply = async (id: string, input: ApplyUpdateInput) => {
    const failure = await act(id, 'apply', input);
    setError(failure);
    if (!failure) onApplied?.();
  };

  return (
    <section className="space-y-2">
      <p className="flex items-center gap-1.5 px-1 text-[12px] text-neutral-500">
        <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
        {pending.length ? `${plural(pending.length, 'suggested update')} from research` : 'Updated from research'}
      </p>

      {error && (
        <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[12.5px] text-primary-700">
          {error}
        </p>
      )}

      <div className="space-y-2">
        {shown.map((update) => (

          <UpdateCard
            key={update.id}
            update={update}
            canEdit={canEdit}
            busy={busy}
            onApply={(input) => void apply(update.id, input)}
            onDismiss={(reason) => void act(update.id, 'dismiss', { reason }).then(setError)}
          />
        ))}
      </div>
    </section>
  );
};
